import { trimEnd } from "lodash-es"
import { observable, runInAction } from "mobx"

import { myPrincipalQueryKey, myProfileQueryKey } from "@/hooks/useProfile.ts"
import { generateRandomString } from "@/lib/client/random.ts"
import { TokenInfo, type TokenSource } from "@/lib/client/WebSocketStableConnection.ts"
import { getLogger } from "@/lib/logging.ts"

import type { QueryClient } from "@tanstack/react-query"
import type { IObservableValue } from "mobx"

export interface AuthController {
  /**
   * Redirects to login page
   * @param provider null for default JBA
   */
  signIn: (provider: string | null) => void
  tokenSource: TokenSource
  authenticated: IObservableValue<boolean>
  authenticatedUser: () => UserInfo | null

  awaitReady: () => Promise<void>
  onAuthRedirected: () => void
  isExpired: () => boolean
  logout: () => Promise<void>
}

const log = getLogger("AuthController")

const MATTER_CLOUD_STORAGE_PREFIX = "dark-matter-web-app_"

function getMatterCloudTokenKey(): string {
  return MATTER_CLOUD_STORAGE_PREFIX + "token"
}

function getMatterCloudExpiresKey(): string {
  return MATTER_CLOUD_STORAGE_PREFIX + "expires"
}

function getMatterCloudStateKey(): string {
  return MATTER_CLOUD_STORAGE_PREFIX + "state"
}

function loadMatterTokenFromLocalStorage(): TokenInfo {
  const token = localStorage.getItem(getMatterCloudTokenKey()) || ""
  const expires = new Date(localStorage.getItem(getMatterCloudExpiresKey()) || "")
  return new TokenInfo(token, expires.getTime())
}

export function createAuthController(params: {
  spaceHost: string
  queryClient: QueryClient
}): AuthController {
  return new MatterCloudAuthControllerImpl(trimEnd(params.spaceHost, "/"), params.queryClient)
}

export type UserInfo = {
  sub: string
  full_name: string
  first_name: string
  last_name: string
  preferred_username: string
  email: string
}

class MatterCloudAuthControllerImpl implements AuthController {
  public tokenSource: CloudTokenSource
  readonly authenticated = observable.box(false)
  cachedUser: UserInfo | null = null

  constructor(readonly spaceHost: string, private readonly queryClient: QueryClient) {
    const tokenInfo = loadMatterTokenFromLocalStorage()

    if (tokenInfo.valid() && !tokenInfo.expired()) {
      runInAction(() => this.authenticated.set(true))
    }

    this.tokenSource = new CloudTokenSource(tokenInfo)
  }

  async awaitReady() {
    await this.tokenSource.token()
    if (!this.cachedUser) {
      this.cachedUser = await this.loadUserInfo()
    }
  }

  authenticatedUser(): UserInfo | null {
    return this.cachedUser
  }

  isExpired() {
    const token = loadMatterTokenFromLocalStorage()
    return token.valid() && token.expired()
  }

  signIn(provider: string | null) {
    const authUrl = new URL(`${this.spaceHost}/oauth/start`)

    if (provider !== null) {
      authUrl.searchParams.set("via_provider", provider)
    }

    const state = generateRandomString(8)
    window.localStorage.setItem(getMatterCloudStateKey(), state)
    authUrl.searchParams.set("state", state)

    const currentUrl = new URL(window.location.href)
    const invitationToken = currentUrl.searchParams.get("invitationToken")
    if (invitationToken) {
      const payload = { invitationToken }
      const json = JSON.stringify(payload)
      authUrl.searchParams.set("state", btoa(json))
    }

    // Only send redirect_host if AUTH_ON_FOREIGN_BACKEND is enabled
    if (import.meta.env.VITE_AUTH_ON_FOREIGN_BACKEND === "true") {
      const currentHost = `${currentUrl.protocol}//${currentUrl.host}`
      authUrl.searchParams.set("redirect_host", currentHost)
    }

    window.location.href = authUrl.toString()

    // throw redirect({
    //   href: authUrl.toString(),
    // })

    // return authUrl.toString()
  }

  async loadUserInfo(): Promise<UserInfo | null> {
    const res = await fetch(this.spaceHost + "/oauth/userinfo", {
      mode: "cors",
      headers: {
        authorization: (await this.tokenSource.token()).accessToken,
      },
    })

    if (!res.ok) {
      if (res.status === 401) {
        throw new NotAuthenticatedError("Token is not valid")
      }
      return null
    }

    return await res.json() as UserInfo
  }

  async logout() {
    try {
      await fetch(this.spaceHost + "/oauth/logout", {
        mode: "cors",
        headers: {
          authorization: (await this.tokenSource.token()).accessToken,
        },
      })
    } catch (_) { /* empty */ }

    this.cleanup()
    void this.queryClient.invalidateQueries({ queryKey: myPrincipalQueryKey })
    void this.queryClient.invalidateQueries({ queryKey: myProfileQueryKey })
  }

  onAuthRedirected() {
    const fragment = window.location.hash.substring(1) // Remove the leading '#'
    const authResponse = this.parseQueryString(fragment)

    // https://youtrack.jetbrains.com/issue/Matter-1426
    try {
      const expected_state = window.localStorage.getItem(getMatterCloudStateKey())
      const actual_state = authResponse.get("state")
      if (expected_state !== actual_state) {
        log.error("OAuth authentication state mismatch")
        this.cleanup()
        return
      }
    } finally {
      window.localStorage.removeItem(getMatterCloudStateKey())
    }

    const token = this.storeSessionId({
      session_id: authResponse.get("session_id"),
      session_ttl: authResponse.get("session_ttl")
    })
    this.tokenSource.setToken(token)
  }

  storeSessionId(parsed: {
    session_id: string | undefined
    session_ttl: string | undefined
  }): TokenInfo {
    const session_id = parsed.session_id
    if (session_id == null) {
      log.error("OAuth authentication response contains no access_token")
      this.cleanup()
      return TokenInfo.Empty()
    }

    const expires_in = parsed.session_ttl

    // https://www.rfc-editor.org/rfc/rfc6749#section-5.1
    // The lifetime in seconds of the access token
    const effectiveExpiresIn = expires_in ? parseInt(expires_in, 10) : (40 * 60)
    const expiresAt = Date.now() + effectiveExpiresIn * 1000

    log.debug("Set access token to local storage")
    window.localStorage.setItem(getMatterCloudTokenKey(), session_id)
    window.localStorage.setItem(getMatterCloudExpiresKey(), new Date(expiresAt).toISOString())

    return new TokenInfo(session_id, expiresAt)
  }

  /**
   * Parses a queryString into an object.
   * ```
   * parseQueryString("access_token=2YotnFZFEjr1zCsicMWpAA&state=xyz&token_type=example&expires_in=3600");
   * // is {access_token: "2YotnFZFEjr1zCsicMWpAA", state: "xyz", token_type: "example", expires_in: "3600"}
   * ```
   * @param {string} queryString query parameter string to parse
   * @return {Map} object with query parameters map
   */
  private parseQueryString(queryString: string | null): Map<string, string> {
    const queryParameters = new Map<string, string>()
    if (queryString != null) {
      const decode = (s: string) => decodeURIComponent(s.replace(/\+/g, " "))

      const parameterPairs = queryString.split("&")
      parameterPairs.forEach(pair => {
        const eqIndex = pair.indexOf("=")
        const name = eqIndex >= 0 ? pair.substring(0, eqIndex) : pair
        const value = eqIndex >= 0 ? pair.substring(eqIndex + 1) : ""

        queryParameters.set(decode(name), decode(value))
      })
    }
    return queryParameters
  }

  private cleanup = () => {
    this.authenticated.set(false)
    this.tokenSource = new CloudTokenSource(TokenInfo.Empty())
    this.cachedUser = null

    localStorage.removeItem(getMatterCloudTokenKey())
    localStorage.removeItem(getMatterCloudExpiresKey())
  }
}

class CloudTokenSource implements TokenSource {
  private subscribers = new Set<(token: TokenInfo) => void>()
  private lastToken: TokenInfo

  constructor(token: TokenInfo) {
    this.lastToken = token
  }

  setToken(token: TokenInfo) {
    this.lastToken = token
    for (const callback of this.subscribers) {
      callback(token)
    }
  }

  async token() {
    const lastToken = this.lastToken
    if (!lastToken.valid()) {
      throw new NotAuthenticatedError("Token is not valid")
    }
    if (lastToken.expiring()) {
      throw new TokenExpiredError("Token is expired")
    }

    return lastToken
  }

  onTokenChanged(callback: (token: TokenInfo) => void) {
    this.subscribers.add(callback)
  }
}

export class NotAuthenticatedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "NotAuthenticatedError"
  }
}

export class TokenExpiredError extends NotAuthenticatedError {
  constructor(message: string) {
    super(message)
    this.name = "TokenExpiredError"
  }
}

export function getStoredAuthToken(): TokenInfo | null {
  const tokenInfo = loadMatterTokenFromLocalStorage()
  if (tokenInfo.valid() && !tokenInfo.expired()) {
    return tokenInfo
  }

  return null
}

export function clearStoredAuthToken(): void {
  localStorage.removeItem(getMatterCloudTokenKey())
  localStorage.removeItem(getMatterCloudExpiresKey())
}